import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { searchCountries } from "@/lib/countries";

/**
 * Structured geographic autocomplete for VOIHU.
 * City/generic search is proxied through /api/geocode/search so the browser
 * never contacts Nominatim directly (no third-party IP exposure, no consent
 * banner needed for this call). Countries use the bundled ISO dataset.
 * Debounced. Fires onSelect with:
 *   { country, country_code, city, region, lat, lon, display_name }
 * Country is required upstream; city is optional. Value is stored as the current
 * text; only a valid selection sets the structured `selected` object.
 */
export default function LocationPicker({ mode, value, onChange, onSelect,
                                         selected, countryCode, testid,
                                         placeholder, autoFocus }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 1) { setItems([]); return; }
    if (selected && (selected.city || selected.country) === q) return;

    // Countries: use bundled ISO dataset for reliable prefix matching (no Nominatim).
    if (mode === "country") {
      const matches = searchCountries(q, 8).map((c, i) => ({
        place_id: `iso-${c.country_code}`,
        _iso: c,
      }));
      setItems(matches);
      setOpen(matches.length > 0);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const params = { q, mode: mode === "city" ? "city" : "generic" };
    if (countryCode) params.countrycodes = countryCode.toLowerCase();

    setLoading(true);
    const t = setTimeout(async () => {
      try {
        // Routed through VOIHU backend proxy — never hits Nominatim
        // from the browser. Backend adds the required User-Agent,
        // enforces the 1 req/s policy, caches 5 minutes, and never
        // persists the query text or client IP.
        const r = await api.get("/geocode/search", {
          params, signal: ctrl.signal,
        });
        const data = r.data;
        setItems(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (_) { /* aborted, rate-limited, or offline */ }
      finally { setLoading(false); }
    }, 260);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [value, mode, countryCode, selected]);

  useEffect(() => {
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (row) => {
    if (row._iso) {
      // Country dataset row
      const c = row._iso;
      const norm = {
        display_name: c.country,
        country: c.country,
        country_code: c.country_code,
        city: "",
        region: "",
        lat: c.lat,
        lon: c.lon,
      };
      onSelect?.(norm);
      onChange?.(norm.country);
      setOpen(false);
      return;
    }
    const addr = row.address || {};
    const countryName = addr.country || row.display_name.split(",").pop().trim();
    const cityName = addr.city || addr.town || addr.village || addr.municipality || addr.hamlet || "";
    const region = addr.state || addr.region || addr.county || "";
    const displayName = mode === "country" ? countryName : row.display_name;
    const norm = {
      display_name: displayName,
      country: countryName,
      country_code: (addr.country_code || "").toUpperCase(),
      city: mode === "city" ? cityName || row.display_name.split(",")[0].trim() : "",
      region,
      lat: Number(row.lat),
      lon: Number(row.lon),
    };
    onSelect?.(norm);
    onChange?.(mode === "country" ? norm.country : norm.city || norm.display_name.split(",")[0].trim());
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        value={value || ""}
        onFocus={() => items.length && setOpen(true)}
        onChange={(e) => { onChange?.(e.target.value); onSelect?.(null); }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="oh-input"
        data-testid={testid}
        autoComplete="off"
      />
      {selected && (
        <div className="oh-label mt-2 text-[color:var(--oh-ink-3)]">
          ✓ {selected.display_name}
        </div>
      )}
      {open && items.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-2 z-30 border oh-hairline bg-[color:var(--oh-bg)] max-h-72 overflow-auto"
             data-testid={`${testid}-list`}>
          {items.map((row) => {
            const label = row._iso
              ? row._iso.country
              : (mode === "country"
                  ? (row.address?.country || row.display_name.split(",").pop().trim())
                  : row.display_name);
            return (
              <button
                key={row.place_id}
                type="button"
                onClick={() => pick(row)}
                className="block w-full text-left px-4 py-3 border-b oh-hairline last:border-b-0 hover:bg-[color:var(--oh-bg-2)] transition-colors"
                data-testid={`${testid}-option-${row.place_id}`}
              >
                <div className="text-sm text-[color:var(--oh-ink)]">{label}</div>
              </button>
            );
          })}
        </div>
      )}
      {loading && !open && (
        <div className="oh-label mt-2 text-[color:var(--oh-ink-3)]">Searching…</div>
      )}
    </div>
  );
}
